import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { ComponentProps, MouseEventHandler, ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "ghost";

interface BaseProps {
  variant?: Variant;
  icon?: boolean;
  children: ReactNode;
  className?: string;
  onClick?: MouseEventHandler;
}

type LinkProps = BaseProps & {
  to: ComponentProps<typeof Link>["to"];
  href?: never;
  type?: never;
};
type AnchorProps = BaseProps & {
  href: string;
  to?: never;
  type?: never;
};
type ButtonProps = BaseProps & {
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  to?: never;
  href?: never;
};

export type CTAButtonProps = LinkProps | AnchorProps | ButtonProps;

const BASE =
  "group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-medium transition-[transform,box-shadow,background-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-primary text-primary-foreground hover:-translate-y-px hover:shadow-[0_14px_30px_-16px_var(--primary)]",
  ghost: "border border-rule text-ink hover:bg-surface",
};

/**
 * Pill-shaped editorial CTA used across home-v4 / home-v5. Renders a
 * TanStack <Link> when `to` is provided, otherwise an <a> or <button>.
 * Includes an animated arrow unless `icon={false}`.
 */
export function CTAButton(props: CTAButtonProps) {
  const variant: Variant = props.variant ?? "primary";
  const icon = props.icon ?? true;
  const disabled = "disabled" in props ? props.disabled : false;
  const classes = cn(
    BASE,
    VARIANTS[variant],
    disabled && "opacity-50 cursor-not-allowed pointer-events-none",
    props.className,
  );

  const content = (
    <>
      {props.children}
      {icon && (
        <ArrowRight
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
          aria-hidden
        />
      )}
    </>
  );

  if ("to" in props && props.to) {
    return (
      <Link to={props.to} onClick={props.onClick} className={classes}>
        {content}
      </Link>
    );
  }
  if ("href" in props && props.href) {
    return (
      <a href={props.href} onClick={props.onClick} className={classes}>
        {content}
      </a>
    );
  }
  return (
    <button
      type={(props as ButtonProps).type ?? "button"}
      onClick={props.onClick}
      disabled={disabled}
      className={classes}
    >
      {content}
    </button>
  );
}
